import React, { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'tippay_employer_install_prompt_dismissed';

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return true;
  const mq = window.matchMedia('(display-mode: standalone)');
  if (mq.matches) return true;
  return window.navigator.standalone === true;
}

function isIosLike() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Login-screen install banner: Chromium (beforeinstallprompt) and iOS (Share → Add to Home Screen).
 */
export default function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (dismissed || isStandaloneDisplay()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      try {
        localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [dismissed]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    setDeferredPrompt(null);
  }, []);

  const onInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      /* user dismissed native sheet or error */
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
    }
  }, [deferredPrompt]);

  if (dismissed || isStandaloneDisplay()) return null;

  const showChromium = Boolean(deferredPrompt);
  const showIos = !showChromium && isIosLike();

  if (!showChromium && !showIos) return null;

  return (
    <div className="emp-install-prompt" role="region" aria-label="Install app">
      <div className="emp-install-prompt-inner">
        <div className="emp-install-prompt-text">
          {showChromium ? (
            <>
              <strong>Install TipPay</strong>
              <span>Add to your home screen for faster access and an app-like experience.</span>
            </>
          ) : (
            <>
              <strong>Add TipPay to Home Screen</strong>
              <span>
                Tap <strong>Share</strong> <span aria-hidden="true">(□↑)</span> then <strong>Add to Home Screen</strong>.
              </span>
            </>
          )}
        </div>
        <div className="emp-install-prompt-actions">
          {showChromium ? (
            <button
              type="button"
              className="emp-install-prompt-install"
              onClick={onInstallClick}
              disabled={installing}
            >
              {installing ? 'Installing…' : 'Install'}
            </button>
          ) : null}
          <button type="button" className="emp-install-prompt-dismiss" onClick={dismiss} aria-label="Dismiss">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
